const { PAYMENT_TYPE, PAYMENT_STATUS, BANK_TRANSFER_REQUEST_STATUS } = require('arbitrip-common/general/constants');
const { RESERVATION_STATUS } = require('@arbitrip/constants/reservations');
const { BANK_TRANSFER_BUSINESS_DAYS_BEFORE_DEADLINE, LOCKING_BANK_TRANSFER_REQUEST_STATUSES } = require('@arbitrip/constants');
const { subtractBusinessDays, hasBusinessDayBuffer } = require('arbitrip-common/general/utils/BusinessDays');

const BANK_TRANSFER_LOCK_TRANSLATION_KEY = {
	[BANK_TRANSFER_REQUEST_STATUS.SUBMITTED]: 'BankTransferInReview',
	[BANK_TRANSFER_REQUEST_STATUS.NEEDS_CORRECTION]: 'BankTransferNeedsCorrection',
	[BANK_TRANSFER_REQUEST_STATUS.CANCELLED_PENDING_REVIEW]: 'BankTransferCancellationPending',
	[BANK_TRANSFER_REQUEST_STATUS.PAID]: 'Paid',
};
const BANK_TRANSFER_LOCK_TRANSLATION_KEY_FALLBACK = 'BankTransferLocked';

function getIsUnpaid(profile, booking) {
	if (!profile || !booking) {
		console.log('nop');
		return false;
	}

	return (
		booking.status === RESERVATION_STATUS.APPROVED &&
		profile.pay_later_allowed &&
		booking?.pay_later_deadline &&
		booking?.payment?.payment_type === PAYMENT_TYPE.PAY_LATER &&
		booking?.payment?.status !== PAYMENT_STATUS.BILLED
	);
}

function getIsBnpl(booking) {
	if (!booking) return false;
	return booking?.payment?.payment_type === PAYMENT_TYPE.BNPL;
}

function getBankTransferDeadline(booking) {
	const payLaterDeadline = booking?.pay_later_deadline;
	if (!payLaterDeadline) return null;
	return subtractBusinessDays(payLaterDeadline, BANK_TRANSFER_BUSINESS_DAYS_BEFORE_DEADLINE).toDate();
}

// Mirrors the server's `packages/bank-transfer/deadline.js` `isAvailable`: the option
// is offered for exactly as long as the deadline shown by `getBankTransferDeadline`
// above has not passed, so the two can never name different days.
function isBankTransferAvailable(profile, booking) {
	return hasBusinessDayBuffer(booking?.pay_later_deadline, BANK_TRANSFER_BUSINESS_DAYS_BEFORE_DEADLINE);
}

function isBankTransferLocked(booking) {
	return LOCKING_BANK_TRANSFER_REQUEST_STATUSES.includes(booking?.bank_transfer_request?.status);
}

function isBankTransferPaid(booking) {
	return booking?.bank_transfer_request?.status === BANK_TRANSFER_REQUEST_STATUS.PAID;
}

function getBankTransferLockTranslationKey(booking) {
	return BANK_TRANSFER_LOCK_TRANSLATION_KEY[booking?.bank_transfer_request?.status] || BANK_TRANSFER_LOCK_TRANSLATION_KEY_FALLBACK;
}

// AR-3488 AC #4 — these four fields arrive from 107-BE's widened aggregation
// projection (packages/mongodb/index.js `getBankTransferRequestPopulate`).
// Before that ticket lands (or for any booking with no bank-transfer request
// at all), `booking.bank_transfer_request` is either absent or carries only
// `status` — every helper below returns `null` in that case, so callers
// degrade gracefully to today's status-only behaviour instead of throwing.
function needsBankTransferCorrection(booking) {
	return booking?.bank_transfer_request?.status === BANK_TRANSFER_REQUEST_STATUS.NEEDS_CORRECTION;
}

function getBankTransferRejectionReason(booking) {
	return booking?.bank_transfer_request?.rejection_reason || null;
}

function getBankTransferRejectionComment(booking) {
	return booking?.bank_transfer_request?.rejection_comment || null;
}

function getBankTransferReferenceNumber(booking) {
	return booking?.bank_transfer_request?.reference_number || null;
}

/**
 * Compute the points-adjusted BNPL amounts from the server's ILS base.
 *
 * The caller owns how the points discount (in ILS) is derived, since the
 * available inputs differ per surface (chosen points × rate, applied discount
 * value, exact points applied, …). This util owns the single source of truth
 * for converting the remaining ILS base into the displayed pay-now / pay-later
 * amounts in both ILS and USD.
 *
 * `pointIlsValue` is the per-point ILS value used to decide `isFullyCovered`:
 * fully covered means the remaining base is below one point's worth (no further
 * point can reduce it) — i.e. the real-zero / full-deal-limit case, as opposed
 * to a value that merely rounds to zero.
 *
 * @returns {null|{adjustedIls:number, isFullyCovered:boolean, payNowIls:number, payLaterIls:number, payNowUsd:(number|null), payLaterUsd:(number|null)}}
 *   `null` when there is no ILS base. USD amounts are `null` when no ILS→USD
 *   rate is available (callers fall back to the server's USD figures).
 */
function computeBnplAmounts({
	bnplIlsAmount,
	bnplIlsRate,
	bnplFeePercentage = 2,
	pointsDiscountIls = 0,
	pointIlsValue = 0,
} = {}) {
	if (bnplIlsAmount == null) {
		return null;
	}
	const adjustedIls = Math.max(0, bnplIlsAmount - (pointsDiscountIls || 0));
	const isFullyCovered = adjustedIls <= 0 || (pointsDiscountIls > 0 && pointIlsValue > 0 && adjustedIls < pointIlsValue);
	const feeMultiplier = 1 + bnplFeePercentage / 100;
	const hasRate = bnplIlsRate > 0;
	return {
		adjustedIls,
		isFullyCovered,
		payNowIls: Math.round(adjustedIls),
		payLaterIls: Math.round(adjustedIls * feeMultiplier),
		payNowUsd: hasRate ? Math.round(adjustedIls / bnplIlsRate) : null,
		payLaterUsd: hasRate ? Math.round((adjustedIls * feeMultiplier) / bnplIlsRate) : null,
	};
}

module.exports = {
	getIsUnpaid,
	getIsBnpl,
	computeBnplAmounts,
	subtractBusinessDays,
	getBankTransferDeadline,
	isBankTransferAvailable,
	isBankTransferLocked,
	isBankTransferPaid,
	getBankTransferLockTranslationKey,
	needsBankTransferCorrection,
	getBankTransferRejectionReason,
	getBankTransferRejectionComment,
	getBankTransferReferenceNumber,
};
